import * as React from 'react'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'

import { HOME_QUERY, LATEST_QUERY, UPCOMING_QUERY } from '../../graphql/graphql-queries'
import { VideoFullData } from '../../interfaces'
import { HomeVideoCard } from '../video/card'
import { SmallSpinner, SmallSpinnerContainer } from '../spinner'
import { useInfiniteLoadIndexer } from '../../hooks/infiniteLoadIndexer'
import { Mint, Query_Root } from '../../graphql/indexer-graphql-types'
import { fbEnrichUsersFromIds, fbEnrichVideosFromIds } from '../../data/FirebaseEnrich'
import { animated, useSpring } from 'react-spring'
import { getMarketplaceData } from '../../data/IndexerUtils'
import moment from 'moment'
import { idComparer } from '../../utils/comparer'

const itemsContainerStyle = `
  display: flex;
  flex-wrap: wrap;
  position: relative;
  z-index: 0;
  /* margin: auto; */
  // mobile
  margin-top: 25px;
  width: 100%;

  // tablet
  @media (min-width: 43em) {
    width: 100%;
  }

  // small desktop
  @media (min-width: 62em) {
    width: 90%;
  }
  
  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

const StyledSkeletonContainer = styled.div`
  ${itemsContainerStyle}
`

const StyledInfiniteScroll = styled(InfiniteScroll)<{
  showGradient: boolean
  viewAll: boolean
}>`
  ${itemsContainerStyle}

  @media (min-width: 43em) {
    max-height: ${(props) => (props.viewAll ? 'none' : '950px')};
    overflow: ${(props) => (props.viewAll ? 'none' : 'hidden')};
  }

  ${(props) =>
    !props.viewAll && props.showGradient
      ? `:after {
    content: '';
    position: absolute;
    z-index: 1;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: -webkit-linear-gradient(
      270deg,
      rgba(16, 16, 16, 0),
      rgba(16, 16, 16, 0),
      rgba(16, 16, 16, 0),
      rgba(16, 16, 16, 0),
      rgba(16, 16, 16, 1)
    );

    @media (min-width: 43em) {
      background: -webkit-linear-gradient(
        270deg,
        rgba(16, 16, 16, 0),
        rgba(16, 16, 16, 0),
        rgba(16, 16, 16, 0),
        rgba(16, 16, 16, 0),
        rgba(16, 16, 16, 0.25),
        rgba(16, 16, 16, 0.75)
      );
    }
  }`
      : ''}
`

const ViewMoreButton = styled(animated.div)`
  border-style: solid;
  border-width: 1px;
  border-color: #fff;
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: 20%;
  right: 20%;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #101010;

  font-size: 14px;
  padding: 10px 0;
  width: calc(60% - 15px);
  border-radius: 10px;
  /* margin-right: 25px; */

  @media (min-width: 43em) {
    left: 30%;
    right: 30%;
    font-size: 18px;
    padding: 15px 0;
    width: calc(40% - 15px);
    border-radius: 15px;
    /* margin-right: 50px; */
  }
`

const Title = styled.div`
  font-size: 30px;
  font-weight: 600;
  white-space: pre-wrap;
  text-align: left;
  width: calc(100% - 40px);
  margin: 5px 20px 20px;
  @media (min-width: 43em) {
    font-size: 48px;
  }

  @media (min-width: 62em) {
    font-size: 42px;
    text-align: left;
  }

  @media (min-width: 82em) {
    font-size: 54px;
  }
`

const parseItems = (data: Query_Root | undefined): Mint[] => {
  const { mint: mints } = data || {}
  if (!mints?.length) {
    return []
  }

  return mints
}

const enrichItems = async (mints: Mint[]): Promise<VideoFullData[]> => {
  if (!mints.length) {
    return []
  }

  const ids = mints.map((mint) => mint.id)
  const enrichedVideos = await fbEnrichVideosFromIds(ids)
  const creators = await fbEnrichUsersFromIds(enrichedVideos.map((video) => video.created_by))
  return enrichedVideos.map((video) => {
    const mint = mints.find((mint) => mint.id === video.address)
    const creator = creators.find((creator) => creator.address === video.created_by)
    const marketplaceData = mint ? getMarketplaceData(mint) : undefined

    return {
      video,
      creator,
      ...marketplaceData,
    }
  })
}

export enum HomeVideosType {
  UPCOMING,
  LATEST,
}

const HomeVideos = ({ type }: { type: HomeVideosType }): JSX.Element => {
  const {
    rawData: originalItems,
    data: items,
    fetchNext,
    hasNext,
  } = useInfiniteLoadIndexer<VideoFullData, Mint>({
    id: '',
    query:
      type === HomeVideosType.UPCOMING
        ? UPCOMING_QUERY
        : process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
        ? LATEST_QUERY
        : HOME_QUERY,
    enrichItems,
    parseItems,
    comparer: idComparer,
    page_size: 10,
    refetchMarker: 'home',
    variables:
      type === HomeVideosType.UPCOMING
        ? {
            now: new Date().toISOString(),
            end: moment(new Date()).add(1, 'month').toISOString(),
          }
        : {
            now: new Date().toISOString(),
          },
  })

  const [viewMoreHover, setViewMoreHover] = React.useState(false)
  const [viewAll, setViewAll] = React.useState(false)

  const viewMoreHoverState = useSpring({
    boxShadow: viewMoreHover ? '0px 5px 25px rgba(255,255,255,0.1)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: viewMoreHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  if (originalItems.length === 0 && hasNext) {
    return (
      <StyledSkeletonContainer>
        <Title>{type === HomeVideosType.UPCOMING ? `NEXT UP` : `LATEST VIDEOS`}</Title>
        {[...Array(6).keys()].map((index) => (
          <HomeVideoCard key={`skeleton-item-${index}`} />
        ))}
      </StyledSkeletonContainer>
    )
  }

  if (items.length === 0) {
    return <></>
  }

  return (
    <StyledInfiniteScroll
      loadMore={fetchNext}
      hasMore={viewAll && hasNext}
      loader={
        <SmallSpinnerContainer>
          <SmallSpinner />
        </SmallSpinnerContainer>
      }
      useWindow
      viewAll={viewAll}
      showGradient={items.length > 3 && !viewAll}
    >
      <Title>{type === HomeVideosType.UPCOMING ? `NEXT UP` : `LATEST VIDEOS`}</Title>
      {items.map(
        (item: VideoFullData, index: number): JSX.Element => (
          <HomeVideoCard item={item} key={`list-item-${index}`} />
        )
      )}
      {hasNext && !viewAll && (
        <ViewMoreButton
          onMouseEnter={() => setViewMoreHover(true)}
          onMouseLeave={() => setViewMoreHover(false)}
          onClick={() => setViewAll(!viewAll)}
          style={viewMoreHoverState}
        >
          {`View All ${type === HomeVideosType.UPCOMING ? 'Upcoming' : 'Latest'} Videos`}
        </ViewMoreButton>
      )}
    </StyledInfiniteScroll>
  )
}

export default HomeVideos
